/**
 * TaxonomyCascader (S1.3 · search-first in S4.2) — net-new shared component.
 *
 * Product-classification picker over the 5-capable category tree (S0.4). Two
 * modes, one value contract:
 *
 *   • SEARCH (default, S4.2) — a single searchable `Select` fed by the flat,
 *     path-labelled `selectablePathOptions` (S4.1). Type a leaf/ancestor name
 *     or a code; every option carries its full "A / B / C" breadcrumb rendered
 *     with middle-truncation that always keeps the LEAF + tooltip-on-overflow,
 *     so a deep path stays legible at the true ~280–320px side-context width.
 *     The bounded tree is already client-side (the cached `useTaxonomyTree`
 *     hook) — there is no `loadData`, so `showSearch` triggers NO network fetch.
 *
 *   • BROWSE — the original per-ENABLED-level cascade, kept VERBATIM behind a
 *     "Browse categories / 瀏覽分類" toggle: one Select per enabled level
 *     (3 by default; L4/L5 appear only when enabled — hide-don't-disable),
 *     per-level placeholders from the renamable bilingual level labels, child
 *     select disabled until its parent is chosen.
 *
 * Value contract (UNCHANGED across both modes): `onChange(categoryId | null)`,
 * leaf-attach (value = the deepest selected node), and the soft-warn when the
 * attached node still has selectable children. Bilingual breadcrumb + the
 * inline-create footer (`onRequestCreate(parentId)`) are shared; in search mode
 * the footer creates at the root (no parent row to anchor — the consumer opens
 * the Wave-3 quick-create popover / CreateNodeModal for a top-level category).
 *
 * Hidden-level read rule: nodes at a DISABLED level are excluded from
 * new-selection options only — an existing `value` sitting on/below a disabled
 * level still renders its full breadcrumb (search mode splices a read-only
 * option for it; browse mode keeps it via the ancestor chain).
 *
 * Data arrives via the shared cached hooks (`useTaxonomyTree` /
 * `useCategoryLevels`, static /api/taxonomy cacheKeys). Mutations never happen
 * here. User-visible text is injected via the `texts` prop (WizardShell
 * precedent); the S4.2 picker strings fall back to the `taxonomy:picker.*`
 * namespace so pre-existing consumers need no change.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { Button, Divider, Empty, Select, Spin, Tooltip, Typography, theme } from 'antd';
import { AppstoreOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  buildPathLabel,
  selectablePathOptions,
  useCategoryLevels,
  useTaxonomyTree,
} from '@/hooks/useTaxonomy';
import { useWizardStyles } from '@/styles/wizardStyles';
import type { CategoryLevelConfig, ProductCategory } from '@/types/api/productCategory';

const { Text } = Typography;

/** The separator `buildPathLabel` emits — split on this to isolate the leaf. */
const PATH_SEPARATOR = ' / ';

export interface TaxonomyCascaderTexts {
  /** Per-level placeholder from the configured (renamable) level label (browse). */
  placeholder: (levelLabel: string) => string;
  /** Inline-create footer label for a level, e.g. 新增{label} (browse). */
  createLabel?: (levelLabel: string) => string;
  /** Soft warning shown when the attached node still has selectable children. */
  nonLeafWarning?: string;
  /** Label prefix for the bilingual breadcrumb line. */
  breadcrumbLabel?: string;
  /** Search-mode combobox placeholder (falls back to `taxonomy:picker.searchPlaceholder`). */
  searchPlaceholder?: string;
  /** Search↔browse toggle label (falls back to `taxonomy:picker.browse`). */
  browseToggle?: string;
  /** Empty-search prompt when no options exist yet (falls back to `taxonomy:picker.emptyPrompt`). */
  emptyPrompt?: string;
  /** Tree-still-hydrating indicator copy (falls back to `taxonomy:picker.loading`). */
  loadingText?: string;
  /** No-match empty state (falls back to `taxonomy:picker.noResults`). */
  noResults?: string;
  /** Search-mode inline-create footer label (falls back to `taxonomy:addRoot`). */
  createRootLabel?: string;
}

export interface TaxonomyCascaderProps {
  /** Selected category id (leaf-preferred; deepest selected node). */
  value?: string | null;
  onChange: (categoryId: string | null) => void;
  /** Validation error state (mirrors antd `status`). */
  status?: 'error';
  disabled?: boolean;
  /**
   * Initial picker surface. `'search'` (default) opens the search-first
   * combobox; `'browse'` opens the per-level cascade. The in-component toggle
   * lets the user switch either way at runtime regardless of this initial value.
   */
  mode?: 'search' | 'browse';
  texts: TaxonomyCascaderTexts;
  /**
   * Present ⇒ each dropdown gets a "+ create" footer. Browse passes the level's
   * `parentId`; search passes `null` (root create). The consumer hosts the
   * quick-create flow and, on success, typically calls `onChange(newNodeId)`.
   */
  onRequestCreate?: (parentId: string | null) => void;
}

interface LevelOption {
  value: string;
  label: string;
  searchText: string;
}

interface RenderedPathOption {
  value: string;
  label: ReactNode;
  searchText: string;
}

const buildOptionLabel = (node: ProductCategory, isZh: boolean): string =>
  isZh ? `${node.nameZh} ${node.nameEn}` : `${node.nameEn} ${node.nameZh}`;

/**
 * Browse-cascade options for one ENABLED level: active children of `parentId`
 * at `depth` (plus the current selection so an inactive-but-selected node keeps
 * rendering), sorted by `sortOrder`. Extracted as a pure function so the S4.2
 * parity test can drive the cascade drill without a DOM — the returned set is
 * byte-for-byte what the per-level `Select` renders.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function cascadeOptionsForDepth(
  nodes: ProductCategory[],
  depth: number,
  parentId: string | null,
  currentSelectionId: string | undefined,
  isZh: boolean
): LevelOption[] {
  const candidates = nodes.filter(
    (n) =>
      n.depth === depth &&
      (depth === 1 ? n.parentId === null : n.parentId === parentId) &&
      (n.isActive || n.id === currentSelectionId)
  );
  return [...candidates]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((n) => ({
      value: n.id,
      label: buildOptionLabel(n, isZh),
      searchText: `${n.code} ${n.nameEn} ${n.nameZh}`.toLowerCase(),
    }));
}

/**
 * Client-side search predicate for the search-mode combobox — matches the typed
 * input against an option's folded `searchText` (code + both names + full path).
 * Pure + exported so the parity test proves search filtering never touches the
 * network (the data is already resident from the cached hooks).
 */
// eslint-disable-next-line react-refresh/only-export-components
export const filterCategoryOption = (
  input: string,
  option?: { searchText?: string }
): boolean => (option?.searchText ?? '').includes(input.trim().toLowerCase());

/** Split a "A / B / C" path into its ancestor `head` and its `leaf` tail. */
// eslint-disable-next-line react-refresh/only-export-components
export const splitPathLeaf = (
  label: string,
  separator: string = PATH_SEPARATOR
): { head: string; leaf: string } => {
  const idx = label.lastIndexOf(separator);
  if (idx < 0) return { head: '', leaf: label };
  return { head: label.slice(0, idx + separator.length), leaf: label.slice(idx + separator.length) };
};

/**
 * Path label with leaf-preserving truncation: the ancestor `head` flex-shrinks
 * and ellipsizes while the `leaf` stays fully visible; the full path shows in a
 * tooltip. Container-width driven (no JS measurement) so it degrades cleanly at
 * ~280–320px.
 */
const PathLabel = ({
  full,
  headColor,
  separator,
}: {
  full: string;
  headColor?: string;
  separator?: string;
}): ReactNode => {
  const { head, leaf } = splitPathLeaf(full, separator);
  return (
    <Tooltip title={full} placement="topLeft" mouseEnterDelay={0.4}>
      <span style={{ display: 'flex', minWidth: 0, alignItems: 'baseline' }}>
        {head && (
          <span
            style={{
              flex: '0 1 auto',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: headColor,
            }}
          >
            {head}
          </span>
        )}
        <span style={{ flex: '0 0 auto', whiteSpace: 'nowrap' }}>{leaf}</span>
      </span>
    </Tooltip>
  );
};

export const TaxonomyCascader = ({
  value,
  onChange,
  status,
  disabled = false,
  mode = 'search',
  texts,
  onRequestCreate,
}: TaxonomyCascaderProps) => {
  const { t, i18n } = useTranslation(['taxonomy', 'common']);
  const { token } = theme.useToken();
  const styles = useWizardStyles();
  const isZh = i18n.language.startsWith('zh');
  const lang: 'en' | 'zh' = isZh ? 'zh' : 'en';

  const [browseMode, setBrowseMode] = useState(mode === 'browse');
  const [searchQuery, setSearchQuery] = useState('');

  const { nodes, isLoading: treeLoading } = useTaxonomyTree();
  const { levels, isLoading: levelsLoading } = useCategoryLevels();

  const byId = useMemo(() => new Map(nodes.map((n) => [n.id, n])), [nodes]);

  const enabledLevels = useMemo<CategoryLevelConfig[]>(
    () => [...levels].filter((level) => level.enabled).sort((a, b) => a.level - b.level),
    [levels]
  );
  const enabledDepths = useMemo(
    () => new Set<number>(enabledLevels.map((level) => level.level)),
    [enabledLevels]
  );

  /** Full ancestor chain of the current value (includes hidden-level nodes). */
  const chain = useMemo<ProductCategory[]>(() => {
    if (!value) return [];
    const target = byId.get(value);
    if (!target) return [];
    return target.path
      .split('/')
      .map((id) => byId.get(id))
      .filter((n): n is ProductCategory => Boolean(n));
  }, [byId, value]);

  const selectedAtDepth = (depth: number): ProductCategory | undefined =>
    chain.find((n) => n.depth === depth);

  const handleLevelChange = (depth: number, nextId: string | undefined) => {
    if (nextId) {
      // Leaf-attach: the deepest selected node IS the value.
      onChange(nextId);
      return;
    }
    // Cleared this level → fall back to the parent (or nothing).
    const parent = selectedAtDepth(depth - 1);
    onChange(parent?.id ?? null);
  };

  // -- Search-mode options: the flat path-labelled set (S4.1), plus a spliced
  //    read-only option for an existing value that sits outside the selectable
  //    set (hidden-level / non-selectable) so it always renders its breadcrumb.
  const searchOptions = useMemo<RenderedPathOption[]>(() => {
    const base = selectablePathOptions(nodes, levels, lang);
    if (value && byId.has(value) && !base.some((o) => o.value === value)) {
      const node = byId.get(value) as ProductCategory;
      const label = buildPathLabel(nodes, value, lang);
      base.push({
        value,
        label,
        searchText: `${node.code} ${node.nameEn} ${node.nameZh} ${label}`.toLowerCase(),
      });
    }
    return base.map((o) => ({
      value: o.value,
      searchText: o.searchText,
      label: <PathLabel full={o.label} headColor={token.colorTextSecondary} />,
    }));
  }, [nodes, levels, lang, value, byId, token.colorTextSecondary]);

  /** Soft warn: attached node still has selectable (active, enabled-level) children. */
  const isNonLeafSelection = useMemo(() => {
    if (!value) return false;
    return nodes.some((n) => n.parentId === value && n.isActive && enabledDepths.has(n.depth));
  }, [enabledDepths, nodes, value]);

  const breadcrumb = useMemo(
    () => chain.map((n) => `${n.nameZh} ${n.nameEn}`).join(' › '),
    [chain]
  );

  const loading = treeLoading || levelsLoading;
  const columnSpanClass = enabledLevels.length > 3 ? 'tw-w-1/4' : 'tw-w-1/3';

  // Picker copy — consumer override first, then the taxonomy:picker.* namespace.
  const searchPlaceholder = texts.searchPlaceholder ?? t('picker.searchPlaceholder');
  const browseToggle = texts.browseToggle ?? t('picker.browse');
  const emptyPrompt = texts.emptyPrompt ?? t('picker.emptyPrompt');
  const loadingText = texts.loadingText ?? t('picker.loading');
  const noResultsText = texts.noResults ?? t('picker.noResults');
  const createRootLabel = texts.createRootLabel ?? t('addRoot');

  const notFoundContent = loading ? (
    <div
      style={{
        padding: token.paddingSM,
        textAlign: 'center',
        color: token.colorTextSecondary,
      }}
    >
      <Spin size="small" style={{ marginInlineEnd: token.marginXS }} />
      {loadingText}
    </div>
  ) : (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description={searchQuery.trim() ? noResultsText : emptyPrompt}
    />
  );

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: token.marginXXS,
        }}
      >
        <Button
          type={browseMode ? 'primary' : 'text'}
          size="small"
          icon={<AppstoreOutlined />}
          aria-pressed={browseMode}
          disabled={disabled}
          onClick={() => setBrowseMode((prev) => !prev)}
        >
          {browseToggle}
        </Button>
      </div>

      {browseMode ? (
        <div className="tw-flex tw-gap-4 tw-flex-wrap">
          {enabledLevels.map((level) => {
            const depth = level.level;
            const parentSelected = depth === 1 || selectedAtDepth(depth - 1) !== undefined;
            const selection = selectedAtDepth(depth);
            const levelLabel = isZh ? level.labelZh : level.labelEn;
            const parent = selectedAtDepth(depth - 1);

            return (
              <div key={depth} className={columnSpanClass} style={{ flex: 1, minWidth: 180 }}>
                <Select<string, LevelOption>
                  size="large"
                  style={styles.formInput}
                  value={selection?.id}
                  placeholder={texts.placeholder(levelLabel)}
                  disabled={disabled || !parentSelected}
                  loading={loading}
                  allowClear
                  showSearch
                  options={cascadeOptionsForDepth(
                    nodes,
                    depth,
                    parent?.id ?? null,
                    selection?.id,
                    isZh
                  )}
                  optionFilterProp="label"
                  filterOption={(input, option) =>
                    (option?.searchText ?? '').includes(input.toLowerCase())
                  }
                  status={status && !value ? 'error' : undefined}
                  onChange={(next) => handleLevelChange(depth, next)}
                  popupRender={
                    onRequestCreate && texts.createLabel
                      ? (menu) => (
                          <>
                            {menu}
                            <Divider style={{ margin: '8px 0' }} />
                            <Button
                              type="text"
                              icon={<PlusOutlined />}
                              style={{ width: '100%', textAlign: 'left' }}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => onRequestCreate(parent?.id ?? null)}
                            >
                              {texts.createLabel?.(levelLabel)}
                            </Button>
                          </>
                        )
                      : undefined
                  }
                />
              </div>
            );
          })}
        </div>
      ) : (
        <Select<string, RenderedPathOption>
          size="large"
          style={styles.formInput}
          value={value ?? undefined}
          placeholder={searchPlaceholder}
          disabled={disabled}
          loading={loading}
          allowClear
          showSearch
          options={searchOptions}
          filterOption={filterCategoryOption}
          onSearch={setSearchQuery}
          notFoundContent={notFoundContent}
          status={status && !value ? 'error' : undefined}
          onChange={(next) => onChange((next as string | undefined) ?? null)}
          popupRender={
            onRequestCreate
              ? (menu) => (
                  <>
                    {menu}
                    <Divider style={{ margin: '8px 0' }} />
                    <Button
                      type="text"
                      icon={<PlusOutlined />}
                      style={{ width: '100%', textAlign: 'left' }}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => onRequestCreate(null)}
                    >
                      {createRootLabel}
                    </Button>
                  </>
                )
              : undefined
          }
        />
      )}

      {breadcrumb && (
        <div
          style={{
            display: 'flex',
            minWidth: 0,
            alignItems: 'baseline',
            gap: 4,
            marginTop: '8px',
            fontSize: token.fontSizeSM,
            color: token.colorTextSecondary,
          }}
        >
          {texts.breadcrumbLabel && (
            <span style={{ flex: '0 0 auto' }}>{texts.breadcrumbLabel}:</span>
          )}
          <PathLabel full={breadcrumb} separator=" › " />
        </div>
      )}

      {isNonLeafSelection && texts.nonLeafWarning && (
        <Text
          style={{
            display: 'block',
            marginTop: '4px',
            fontSize: token.fontSizeSM,
            color: token.colorWarning,
          }}
        >
          {texts.nonLeafWarning}
        </Text>
      )}
    </div>
  );
};

export default TaxonomyCascader;
